import {
  expectedIcDph,
  formatCents,
  formatDecimal,
  icDphMatchesDic,
  isValidDic,
  isValidIco,
  parseMoneyToCents,
  tt,
  type MoneyInput,
} from '@agos/shared'

/**
 * An invoice this restaurant sends out — a tax document, so the arithmetic is the page.
 *
 * **FB-088.** Legacy computes each line with `+(x).toFixed(2)` and then sums the rounded
 * floats:
 *
 * ```js
 * next.lineNet   = +(next.qty * next.unitPrice).toFixed(2)
 * next.lineVat   = +(next.lineNet * (next.vatRate / 100)).toFixed(2)
 * next.lineGross = +(next.lineNet + next.lineVat).toFixed(2)
 * // …
 * net:   lines.reduce((s, l) => s + l.lineNet,   0)
 * vat:   lines.reduce((s, l) => s + l.lineVat,   0)
 * gross: lines.reduce((s, l) => s + l.lineGross, 0)
 * ```
 *
 * Three lines of 0,10 € sum to `0.6000000000000001`. The screen hides it because
 * `fmtEur` rounds again on the way out — but that float is what gets **posted and
 * stored** as the invoice's total. §14.7 exists for exactly this: money is integer cents
 * from the moment it is read to the moment it is printed.
 *
 * Per-line rounding is kept, deliberately. Slovak practice permits either per-line or
 * whole-invoice VAT rounding and they differ by a cent — three lines of 0,03 € at 19 %
 * come to 0,03 € of VAT per line and 0,02 € computed on the whole. Changing it would
 * change figures on documents already sent, so what changes is only that the sums stop
 * drifting.
 */

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled'

export interface InvoiceRow {
  id?: number
  invoiceNumber?: string
  customerName?: string
  issueDate?: string
  dueDate?: string
  paidAt?: string | null
  totalNet?: MoneyInput
  totalVat?: MoneyInput
  totalGross?: MoneyInput
  currency?: string
  status?: string
  notes?: string | null
}

export interface InvoiceLine {
  id?: number
  itemName?: string
  /** A count, not money — 12 pizzas, or 2,5 kg. */
  qty?: number
  unit?: string
  /** A per-unit price. A rate, so it keeps its decimals (FB-056). */
  unitPrice?: number
  /** A percentage. Also a rate. */
  vatRate?: number
  lineNet?: MoneyInput
  lineVat?: MoneyInput
  lineGross?: MoneyInput
  sortOrder?: number
}

export interface InvoiceDetail extends InvoiceRow {
  customerAddress?: string | null
  customerIco?: string | null
  customerDic?: string | null
  customerIcDph?: string | null
  customerEmail?: string | null
  deliveryDate?: string | null
  variableSymbol?: string | null
  constantSymbol?: string | null
  specificSymbol?: string | null
  lines?: InvoiceLine[]
}

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: tt('Koncept'),
  sent: tt('Odoslaná'),
  paid: tt('Zaplatená'),
  cancelled: tt('Zrušená'),
}

export type BadgeColor = 'zinc' | 'blue' | 'emerald' | 'red' | 'amber'

const STATUS_COLORS: Record<InvoiceStatus, BadgeColor> = {
  draft: 'zinc',
  sent: 'blue',
  paid: 'emerald',
  cancelled: 'red',
}

export function statusLabel(status: string | undefined): string {
  if (!status) return ''
  return STATUS_LABELS[status as InvoiceStatus] ?? status
}

export function statusColor(status: string | undefined): BadgeColor {
  return STATUS_COLORS[status as InvoiceStatus] ?? 'zinc'
}

// ─── the line arithmetic, in cents ───────────────────────────────────────────

export interface LineTotals {
  netCents: number
  vatCents: number
  grossCents: number
}

/**
 * One line's net, VAT and gross — each rounded once, in cents.
 *
 * The quantity and the unit price are both multiplicands and keep every decimal they
 * were given; only the product becomes money (FB-056).
 */
export function lineTotals(line: InvoiceLine): LineTotals {
  const qty = line.qty ?? 0
  const unitPrice = line.unitPrice ?? 0
  const rate = line.vatRate ?? 0

  const netCents = roundCents(qty * unitPrice * 100)
  const vatCents = roundCents(netCents * (rate / 100))
  return { netCents, vatCents, grossCents: netCents + vatCents }
}

function roundCents(value: number): number {
  if (!Number.isFinite(value)) return 0
  return value < 0 ? -Math.round(-value) : Math.round(value)
}

export interface InvoiceTotals extends LineTotals {
  lineCount: number
}

/**
 * The invoice's totals — integer sums of the per-line figures.
 *
 * `grossCents` is `netCents + vatCents` rather than a third independent sum, so the
 * three figures on the document cannot disagree with each other whatever the lines do.
 */
export function invoiceTotals(lines: InvoiceLine[]): InvoiceTotals {
  let netCents = 0
  let vatCents = 0
  for (const line of lines) {
    const totals = lineTotals(line)
    netCents += totals.netCents
    vatCents += totals.vatCents
  }
  return { netCents, vatCents, grossCents: netCents + vatCents, lineCount: lines.length }
}

/** The totals as the server stored them, for comparing against the lines. */
export function storedTotals(invoice: InvoiceRow): LineTotals {
  return {
    netCents: parseMoneyToCents(invoice.totalNet),
    vatCents: parseMoneyToCents(invoice.totalVat),
    grossCents: parseMoneyToCents(invoice.totalGross),
  }
}

/**
 * Whether the stored totals match the lines, to the cent.
 *
 * A tax document whose header disagrees with its own lines is one somebody has to look
 * at; smoothing it over is how it stays unnoticed — the rule FB-064 set on the receipt.
 */
export function totalsAgree(invoice: InvoiceDetail): boolean {
  const stored = storedTotals(invoice)
  const computed = invoiceTotals(invoice.lines ?? [])
  return (
    stored.netCents === computed.netCents &&
    stored.vatCents === computed.vatCents &&
    stored.grossCents === computed.grossCents
  )
}

// ─── the VAT recapitulation ──────────────────────────────────────────────────

export interface VatBand {
  rate: number
  netCents: number
  vatCents: number
  grossCents: number
}

/**
 * The base and VAT per rate — what a Slovak invoice has to show.
 *
 * **FB-089.** Legacy's printed invoice shows one *Cena bez DPH / DPH / Spolu* triple and
 * no per-rate split at all. On a single-rate invoice that is the same thing; on a mixed
 * one — 5 % for food eaten in beside 19 % for a delivery, which this kitchen bills both
 * of — it is a recapitulation the document is required to carry and does not.
 *
 * Ordered by rate ascending, which is the order a Slovak invoice conventionally prints.
 */
export function vatBands(lines: InvoiceLine[]): VatBand[] {
  const bands = new Map<number, VatBand>()

  for (const line of lines) {
    const rate = line.vatRate ?? 0
    const totals = lineTotals(line)
    const band = bands.get(rate) ?? { rate, netCents: 0, vatCents: 0, grossCents: 0 }
    band.netCents += totals.netCents
    band.vatCents += totals.vatCents
    band.grossCents += totals.grossCents
    bands.set(rate, band)
  }

  return [...bands.values()].sort((left, right) => left.rate - right.rate)
}

/** Whether the invoice mixes rates, and so needs the recapitulation to be readable. */
export function isMixedRate(lines: InvoiceLine[]): boolean {
  return vatBands(lines).length > 1
}

// ─── the customer's identifiers ──────────────────────────────────────────────

export type CustomerProblem =
  | { kind: 'no-name' }
  | { kind: 'ico-checksum'; value: string }
  | { kind: 'dic-length'; value: string }
  | { kind: 'ic-dph-mismatch'; icDph: string; expected: string }

/**
 * What is wrong with who the invoice is addressed to.
 *
 * A tax document quotes the customer's IČO and DIČ, and both carry checks — the same
 * ones FB-080 applies to a supplier. `Mesto Šurany`'s `00309231` passes: 66 mod 11 is 0,
 * which maps to a check digit of 1.
 *
 * Reported, not refused, except for the name: a private customer has no IČO at all.
 */
export function customerProblems(invoice: InvoiceDetail): CustomerProblem[] {
  const problems: CustomerProblem[] = []
  if ((invoice.customerName ?? '').trim() === '') problems.push({ kind: 'no-name' })

  const ico = (invoice.customerIco ?? '').trim()
  if (ico !== '' && !isValidIco(ico)) problems.push({ kind: 'ico-checksum', value: ico })

  const dic = (invoice.customerDic ?? '').trim()
  if (dic !== '' && !isValidDic(dic)) problems.push({ kind: 'dic-length', value: dic })

  const icDph = (invoice.customerIcDph ?? '').trim()
  if (icDph !== '' && !icDphMatchesDic(icDph, dic)) {
    problems.push({ kind: 'ic-dph-mismatch', icDph, expected: expectedIcDph(dic) })
  }

  return problems
}

// ─── dates, symbols, filters ─────────────────────────────────────────────────

/** Postgres prints `2026-05-05 10:00:00+00`; `Date.parse` refuses it twice. */
export function parseStamp(value: string | null | undefined): number | null {
  const text = (value ?? '').trim()
  if (text === '') return null
  const parsed = Date.parse(text.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * How many days overdue, or `null` when it is not.
 *
 * Counted from the due date on an unpaid invoice only — a paid one is not overdue however
 * late it was, and legacy shows nothing about either.
 */
export function daysOverdue(invoice: InvoiceRow, now: number): number | null {
  if (invoice.status === 'paid' || invoice.status === 'cancelled') return null
  if ((invoice.paidAt ?? null) !== null) return null

  const due = Date.parse(`${invoice.dueDate ?? ''}T23:59:59`)
  if (!Number.isFinite(due) || due >= now) return null
  return Math.floor((now - due) / 86_400_000)
}

/**
 * The variable symbol a payer quotes.
 *
 * `OUT-2026-0002` becomes `20260002` in the data — the year and the sequence, digits
 * only, which is what a bank transfer field accepts.
 */
export function variableSymbolFor(invoiceNumber: string): string {
  return invoiceNumber.replace(/\D/g, '')
}

export interface InvoiceFilter {
  status: string
  search: string
}

export function matchesSearch(invoice: InvoiceRow, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [invoice.invoiceNumber, invoice.customerName, invoice.notes].some((field) =>
    String(field ?? '').toLowerCase().includes(needle),
  )
}

export function filterInvoices(invoices: InvoiceRow[], { status, search }: InvoiceFilter): InvoiceRow[] {
  return invoices.filter((invoice) => {
    if (status !== 'all' && invoice.status !== status) return false
    return matchesSearch(invoice, search)
  })
}

/** Overdue first, then unpaid, then by issue date descending. */
export function byAttention(invoices: InvoiceRow[], now: number): InvoiceRow[] {
  return [...invoices].sort((left, right) => {
    const leftLate = daysOverdue(left, now) ?? -1
    const rightLate = daysOverdue(right, now) ?? -1
    if (leftLate !== rightLate) return rightLate - leftLate

    const leftPaid = left.status === 'paid' ? 1 : 0
    const rightPaid = right.status === 'paid' ? 1 : 0
    if (leftPaid !== rightPaid) return leftPaid - rightPaid

    return (right.issueDate ?? '').localeCompare(left.issueDate ?? '')
  })
}

export interface ListTotals {
  count: number
  grossCents: number
  unpaidCents: number
  overdueCount: number
  overdueCents: number
}

export function listTotals(invoices: InvoiceRow[], now: number): ListTotals {
  let grossCents = 0
  let unpaidCents = 0
  let overdueCount = 0
  let overdueCents = 0

  for (const invoice of invoices) {
    const gross = parseMoneyToCents(invoice.totalGross)
    grossCents += gross
    if (invoice.status !== 'paid' && invoice.status !== 'cancelled') unpaidCents += gross
    if (daysOverdue(invoice, now) !== null) {
      overdueCount += 1
      overdueCents += gross
    }
  }

  return { count: invoices.length, grossCents, unpaidCents, overdueCount, overdueCents }
}

// ─── the draft form ──────────────────────────────────────────────────────────

export interface LineForm {
  itemName: string
  qty: string
  unit: string
  unitPrice: string
  vatRate: string
}

export function emptyLine(defaultRate: string): LineForm {
  return { itemName: '', qty: '1', unit: 'ks', unitPrice: '0', vatRate: defaultRate }
}

export function lineOf(form: LineForm): InvoiceLine {
  return {
    itemName: form.itemName.trim(),
    qty: numberOf(form.qty),
    unit: form.unit.trim(),
    unitPrice: numberOf(form.unitPrice),
    vatRate: numberOf(form.vatRate),
  }
}

function numberOf(raw: string): number {
  const parsed = Number(raw.trim().replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

export type DraftProblem =
  | { kind: 'no-customer' }
  | { kind: 'no-lines' }
  | { kind: 'line-no-name'; index: number }
  | { kind: 'line-no-qty'; index: number }
  | { kind: 'no-total' }

export function draftProblems(customerName: string, lines: LineForm[]): DraftProblem[] {
  const problems: DraftProblem[] = []
  if (customerName.trim() === '') problems.push({ kind: 'no-customer' })
  if (lines.length === 0) problems.push({ kind: 'no-lines' })

  lines.forEach((line, index) => {
    if (line.itemName.trim() === '') problems.push({ kind: 'line-no-name', index })
    else if (numberOf(line.qty) <= 0) problems.push({ kind: 'line-no-qty', index })
  })

  if (lines.length > 0 && invoiceTotals(lines.map(lineOf)).grossCents <= 0) {
    problems.push({ kind: 'no-total' })
  }

  return problems
}

export function canIssue(problems: DraftProblem[]): boolean {
  return problems.length === 0
}

/** `2026-07-30` plus 14 days. Date arithmetic; nothing to round. */
export function addDays(date: string, days: number): string {
  const parsed = new Date(`${date}T12:00:00`)
  if (Number.isNaN(parsed.getTime())) return date
  parsed.setDate(parsed.getDate() + days)
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`
}

/** `12,5 ks` — a quantity, never padded to two decimals. */
export function formatQty(qty: number | undefined): string {
  const value = qty ?? 0
  const rounded = Math.round(value * 1000) / 1000
  return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',')
}

/** A per-unit price keeps its decimals; only a total is money. */
export function formatUnitPrice(price: number | undefined): string {
  const value = price ?? 0
  return `${String(Math.round(value * 10_000) / 10_000).replace('.', ',')} €`
}

export { formatCents }

/** Who is issuing the invoice, as far as the manager can currently know — §9.137. */
export interface InvoiceIssuer {
  name?: string | null
  address?: string | null
  /** §9.153 — from `settings/billing`, which is *Nastavenia → Fakturácia*. */
  ico?: string | null
  dic?: string | null
  icDph?: string | null
  iban?: string | null
}

/**
 * Escapes a value for the printed sheet — §9.137.
 *
 * <p>The customer's name, address and the invoice's notes are free text somebody typed, and the
 * document is assembled as an HTML string. This is the one place in this module where that is true,
 * so it is the one place that needs this.
 */
export function escapeHtml(value: string): string {
  return value.replace(
    /[<>&"']/g,
    (character) =>
      ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' })[character] ?? character,
  )
}

/**
 * What the printed sheet is still missing — §9.137.
 *
 * <p>A Slovak invoice must identify **both** parties. The detail endpoint returns the customer's
 * identifiers and nothing about the issuer, and no endpoint exposes the franchise's IČO, DIČ or
 * IČ DPH. Legacy prints no supplier block at all, so this is not a regression — but printing a
 * legally incomplete document without saying so would be. `OWNER-QUESTIONS.md` §6 asks where they
 * should come from.
 */
export function issuerProblems(issuer: InvoiceIssuer): string[] {
  const problems: string[] = []
  if (!(issuer.name ?? '').trim()) problems.push(tt('názov firmy'))
  if (!(issuer.address ?? '').trim()) problems.push(tt('adresa firmy'))
  // §9.153 — named one by one now. This used to push *IČO, DIČ a IČ DPH* unconditionally, because
  // nothing served them; a warning that cannot go away teaches people to ignore it.
  if (!(issuer.ico ?? '').trim()) problems.push(tt('IČO'))
  if (!(issuer.dic ?? '').trim()) problems.push(tt('DIČ'))
  if (!(issuer.iban ?? '').trim()) problems.push('IBAN')
  // **IČ DPH is not required and its absence is not a defect** — a company that is not registered
  // for VAT has none, and demanding it would put a permanent warning on a correct invoice.
  return problems
}

/**
 * The issuer's identifiers as the sheet prints them — §9.153.
 *
 * <p>Each is printed only when stored. Nothing is derived: `expectedIcDph` exists and could produce
 * an IČ DPH from a DIČ, but a derived tax number on a tax document is a guess wearing a fact's
 * clothes, and the one place it must not appear is the document itself.
 */
export function issuerIds(issuer: InvoiceIssuer): string {
  const rows: string[] = []
  if ((issuer.ico ?? '').trim()) rows.push(`IČO: ${escapeHtml(String(issuer.ico))}`)
  if ((issuer.dic ?? '').trim()) rows.push(`DIČ: ${escapeHtml(String(issuer.dic))}`)
  if ((issuer.icDph ?? '').trim()) rows.push(`IČ DPH: ${escapeHtml(String(issuer.icDph))}`)
  if ((issuer.iban ?? '').trim()) rows.push(`IBAN: ${escapeHtml(String(issuer.iban))}`)
  return rows.length === 0 ? '' : `<br>${rows.join('<br>')}`
}

/**
 * The invoice as a printable sheet — §9.137, the owner's point 26.
 *
 * <p>Legacy's template with two corrections: the **per-rate VAT recapitulation** FB-089 filed
 * against it, and a supplier block, which legacy omits entirely. The bands come from `vatBands`
 * rather than a second copy of the arithmetic, so the sheet cannot disagree with the dialog.
 */
export function invoiceHtml(invoice: InvoiceDetail, issuer: InvoiceIssuer): string {
  const lines = invoice.lines ?? []
  const totals = invoiceTotals(lines)
  const bands = vatBands(lines)
  const currency = invoice.currency ?? 'EUR'
  const money = (cents: number) => formatCents(cents)
  const text = (value: string | null | undefined) => escapeHtml((value ?? '').toString())

  const customerRows = [
    invoice.customerAddress,
    invoice.customerIco ? `IČO: ${invoice.customerIco}` : null,
    invoice.customerDic ? `DIČ: ${invoice.customerDic}` : null,
    invoice.customerIcDph ? `IČ DPH: ${invoice.customerIcDph}` : null,
  ]
    .filter((row): row is string => Boolean(row))
    .map((row) => `${text(row)}<br>`)
    .join('')

  const lineRows = lines
    .map(
      (line) =>
        `<tr><td>${text(line.itemName)}</td>` +
        `<td class="r">${formatDecimal(Number(line.qty ?? 0), 2)}</td>` +
        `<td>${text(line.unit)}</td>` +
        `<td class="r">${money(parseMoneyToCents(line.unitPrice ?? 0))}</td>` +
        `<td class="r">${formatDecimal(Number(line.vatRate ?? 0), 0)} %</td>` +
        `<td class="r">${money(parseMoneyToCents(line.lineGross ?? 0))}</td></tr>`,
    )
    .join('')

  // FB-089 — the split legacy's sheet does not print. One rate makes one row, which is honest
  // rather than redundant: it says the invoice really is single-rate.
  const bandRows = bands
    .map(
      (band) =>
        `<tr><td class="r">${formatDecimal(band.rate, 0)} %</td>` +
        `<td class="r">${money(band.netCents)}</td>` +
        `<td class="r">${money(band.vatCents)}</td>` +
        `<td class="r">${money(band.grossCents)}</td></tr>`,
    )
    .join('')

  return `<!DOCTYPE html><html lang="sk"><head><meta charset="utf-8">
<title>${tt('Faktúra')} ${text(invoice.invoiceNumber)}</title>
<style>
body { font-family: Arial, sans-serif; margin: 24px; font-size: 13px; color: #111; }
h1 { font-size: 20px; margin: 0 0 8px; }
.head { display: flex; justify-content: space-between; gap: 24px; }
.parties { display: flex; justify-content: space-between; gap: 24px; margin-top: 16px; }
table { width: 100%; border-collapse: collapse; margin-top: 16px; }
th, td { padding: 6px 8px; text-align: left; }
thead { background: #f3f3f3; border-bottom: 2px solid #888; }
tbody tr { border-bottom: 1px solid #ddd; }
.r { text-align: right; }
.totals { margin-top: 12px; text-align: right; }
.grand { font-size: 16px; font-weight: bold; border-top: 2px solid #111; padding-top: 6px; }
.recap { margin-top: 16px; }
.recap caption { text-align: left; font-weight: bold; padding-bottom: 4px; }
@media print { body { margin: 0; padding: 16px; } }
</style></head><body>
<div class="head">
  <div>
    <h1>${tt('FAKTÚRA č.')} ${text(invoice.invoiceNumber)}</h1>
    <p><strong>${tt('Variabilný symbol')}:</strong> ${text(invoice.variableSymbol)}</p>
    <p><strong>${tt('Konštantný symbol')}:</strong> ${text(invoice.constantSymbol)}</p>
  </div>
  <div>
    <p><strong>${tt('Vystavená')}:</strong> ${text(invoice.issueDate)}</p>
    <p><strong>${tt('Splatná')}:</strong> ${text(invoice.dueDate)}</p>
    ${invoice.deliveryDate ? `<p><strong>${tt('Dodaná')}:</strong> ${text(invoice.deliveryDate)}</p>` : ''}
  </div>
</div>
<div class="parties">
  <div><strong>${tt('Dodávateľ')}:</strong><br>${text(issuer.name)}<br>${text(issuer.address)}${issuerIds(issuer)}</div>
  <div><strong>${tt('Odberateľ')}:</strong><br>${text(invoice.customerName)}<br>${customerRows}</div>
</div>
<table>
<thead><tr><th>${tt('Názov')}</th><th class="r">${tt('Mn.')}</th><th>${tt('Jed.')}</th>
<th class="r">${tt('Cena/J')}</th><th class="r">DPH</th><th class="r">${tt('Spolu')}</th></tr></thead>
<tbody>${lineRows}</tbody>
</table>
<table class="recap">
<caption>${tt('Rekapitulácia DPH')}</caption>
<thead><tr><th class="r">${tt('Sadzba')}</th><th class="r">${tt('Základ')}</th>
<th class="r">DPH</th><th class="r">${tt('Spolu')}</th></tr></thead>
<tbody>${bandRows}</tbody>
</table>
<div class="totals">
  <p>${tt('Cena bez DPH')}: ${money(totals.netCents)} ${text(currency)}</p>
  <p>DPH: ${money(totals.vatCents)} ${text(currency)}</p>
  <p class="grand">${tt('Spolu na úhradu')}: ${money(totals.grossCents)} ${text(currency)}</p>
</div>
${invoice.notes ? `<p style="margin-top:24px"><em>${text(invoice.notes)}</em></p>` : ''}
</body></html>`
}
